import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Label,
  TextInput,
  Card,
} from "flowbite-react";
import { useStore } from "../../store";

import {
  ME,
  FETCH_USERS_BY_COMPANY_ID,
  DELETE_USER,
  SET_SUPERUSER_STATUS,
  UPDATE_COMPANY_NAME,
  UPDATE_COMPANY_DOMAIN,
  UPDATE_USER_NAME,
} from "../../lib/urls";
import ActionBar from "../../components/Settings/ActionBar";
import ShortlistDigestCard from "../../components/Settings/ShortlistDigestCard";
import { Heading4 } from "../../lib/ui/components/typography/Typography";
import { toast } from "sonner";

/** Flowbite Card defaults center content vertically; settings pages should top-align. */
const settingsCardTheme = {
  root: {
    children: "flex h-full flex-col justify-start gap-4 px-6 pt-5 pb-6",
  },
};

function SettingsPage() {
  const uid = useStore((state) => state.uid);
  const token = useStore((state) => state.token);
  const user = useStore((state) => state.user);
  const setUser = useStore((state) => state.setUser);

  const [formData, setFormData] = useState({
    email: user?.email || "",
    name: user?.name || "",
    company: user?.company || "",
  });

  // Track original form data to detect changes
  const [originalFormData, setOriginalFormData] = useState({
    email: user?.email || "",
    name: user?.name || "",
    company: user?.company || "",
  });

  const [adminFormData, setAdminFormData] = useState({
    email: user?.email || "",
    companyName: user.company?.name || "",
    companyDomain: user.company?.company_domain || "",
  });

  const [isAdmin, setIsAdmin] = useState(true);
  const [users, setUsers] = useState([]);
  const navigate = useNavigate();
  const [companyId, setCompanyId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const checkAdminStatus = useCallback(async () => {
    try {
      const response = await fetch(ME, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        const user_data = data.user;
        const company_data = data.company;
        const newFormData = {
          email: user_data?.email || "",
          name: user_data?.name || "",
          company: company_data?.name || "",
        };
        setFormData(newFormData);
        setOriginalFormData(newFormData);
      } else {
        console.error("Error checking admin status:", response.statusText);
      }
    } catch (error) {
      console.error("Error checking admin status:", error);
    }
  }, [uid, token]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSave = async () => {
    setIsSaving(true);
    toast.info("Saving changes...");
    
    // Profile page only handles user form data
    const nameUrl = UPDATE_USER_NAME(uid, formData.name);
    try {
      const nameResponse = await fetch(nameUrl, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (nameResponse.ok) {
        toast.success("User name updated successfully!");
        // Update original data after successful save
        setOriginalFormData({ ...formData });
        setUser({
          ...user,
          name: formData.name
        });
        checkAdminStatus();
      } else {
        const data = await nameResponse.json();
        toast.error(`Failed to update user name: ${data.detail}`);
      }
    } catch (error) {
      console.error("Error updating user name:", error);
      toast.error("An error occurred while updating the user name.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscard = () => {
    // Reset form data to original values
    setFormData(originalFormData);
  };

  // Check if form has been modified (email is readonly, so only check name)
  const hasChanges = () => {
    return formData.name !== originalFormData.name;
  };

  const handleRemoveUser = async (userId) => {
    const url = DELETE_USER(userId);
    try {
      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        setUsers((prevUsers) => prevUsers.filter((user) => user.id !== userId));
        toast.success("User removed successfully!");
      } else {
        const data = await response.json();
        toast.error(`Failed to remove user: ${data.detail}`);
      }
    } catch (error) {
      console.error("Error removing user:", error);
      toast.error("An error occurred while removing the user.");
    }
  };

  const handleSetSuperuserStatus = async (userId, isSuperuser) => {
    const url = SET_SUPERUSER_STATUS(userId, isSuperuser);
    try {
      const response = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        setUsers((prevUsers) =>
          prevUsers.map((user) =>
            user.id === userId ? { ...user, is_superuser: isSuperuser } : user
          )
        );
        toast.success(
          `User ${isSuperuser ? "promoted to admin" : "demoted to member"
          } successfully!`
        );
      } else {
        const data = await response.json();
        toast.error(`Failed to update user status: ${data.detail}`);
      }
    } catch (error) {
      console.error("Error updating user status:", error);
      toast.error("An error occurred while updating the user status.");
    }
  };

  return (
    <div className="flex flex-col items-center p-8 bg-gray-100 min-h-screen">
      <ActionBar
        handleDiscard={handleDiscard}
        handleSave={handleSave}
        hasChanges={hasChanges()}
        isSaving={isSaving}
      />

      <Card className="w-[1200px] h-auto" theme={settingsCardTheme}>
        <Heading4 className="mb-4">User Information</Heading4>
        <form className="flex flex-col gap-4">
          {/* Email Input */}
          <div>
            <Label htmlFor="email" className="mb-2 text-body-lg">
              Company Email
            </Label>
            <div className="mb-2"></div>
            <TextInput
              id="email"
              name="email"
              type="email"
              value={formData.email || ""}
              onChange={handleChange}
              placeholder="Enter your email"
              required
              readOnly
            />
          </div>

          {/* Name Input */}
          <div>
            <Label htmlFor="name" className="mb-2 text-body-lg">
              Name
            </Label>
            <div className="mb-2"></div>
            <TextInput
              id="name"
              name="name"
              type="text"
              value={formData.name || ""}
              onChange={handleChange}
              placeholder="Enter your name"
              required
            />
          </div>
        </form>
      </Card>

      <Card className="w-[1200px] h-auto mt-6" theme={settingsCardTheme}>
        <ShortlistDigestCard token={token} />
      </Card>
    </div>
  );
}

export default SettingsPage;
